import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, LineChart, Pencil, Trash2 } from "lucide-react";
import { SectionCard } from "@/components/common/SectionCard";
import { EmptyState } from "@/components/common/EmptyState";
import { EntryModal } from "@/components/common/EntryModal";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { UpdatesHistory } from "@/components/product/UpdatesHistory";
import { forecastFields } from "./financeFields";
import { gbp2 as gbp, monthLabel } from "@/data/financeOptions";

const n = (v) => Number(v) || 0;
const addMonths = (ym, i) => {
  if (!ym || !/^\d{4}-\d{2}/.test(ym)) return `Month ${i + 1}`;
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1 + i, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

const buildRows = (f) => {
  const months = Math.max(1, Math.min(36, n(f.duration_months) || 6));
  let opening = n(f.opening_balance);
  const rows = [];
  for (let i = 0; i < months; i++) {
    const revenue = n(f.expected_revenue), funding = n(f.expected_funding_inflows);
    const totalIn = revenue + funding;
    const sal = n(f.expected_founder_salaries), free = n(f.expected_freelancer_costs), mkt = n(f.expected_marketing), sw = n(f.expected_software);
    const other = n(f.expected_operating_costs) + n(f.expected_other);
    const oneoff = i === 0 ? n(f.expected_oneoff_costs) : 0;
    const totalOut = sal + free + mkt + sw + other + oneoff;
    const closing = opening + totalIn - totalOut;
    rows.push({ month: addMonths(f.start_month, i), opening, revenue, funding, totalIn, sal, free, mkt, sw, other, oneoff, totalOut, closing });
    opening = closing;
  }
  return rows;
};

export const ForecastPanel = ({ onChange }) => {
  const [items, setItems] = useState([]);
  const [selId, setSelId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false); const [editing, setEditing] = useState(null);
  const [confirmId, setConfirmId] = useState(null);

  const load = useCallback(async () => {
    try { const f = await api.list("cash-forecasts"); setItems(f); setSelId((prev) => prev && f.some((x) => x.id === prev) ? prev : (f[0]?.id ?? null)); }
    catch { setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => items.find((f) => f.id === selId) || null, [items, selId]);
  const rows = useMemo(() => (selected ? buildRows(selected) : []), [selected]);

  const openAdd = () => { setEditing({ duration_months: "6", opening_balance: "0", start_month: "" }); setModalOpen(true); };
  const openEdit = () => { setEditing(selected); setModalOpen(true); };
  const save = async (v) => { if (v.id) { const u = await api.update("cash-forecasts", v.id, v); toast.success("Saved"); setSelId(u.id); } else { const c = await api.create("cash-forecasts", v); toast.success("Forecast added"); setSelId(c.id); } await load(); onChange?.(); };
  const doDelete = async () => { await api.remove("cash-forecasts", confirmId); toast.success("Deleted"); setConfirmId(null); setSelId(null); await load(); onChange?.(); };

  const closingEnd = rows.length ? rows[rows.length - 1].closing : 0;
  const negativeMonth = rows.find((r) => r.closing < 0);

  return (
    <>
      <SectionCard testid="forecast-card" title="Cash Forecast" subtitle="Simple month-by-month cash projection from your assumptions"
        action={
          <div className="flex items-center gap-2">
            {items.length > 0 && (
              <Select value={selId || ""} onValueChange={setSelId}><SelectTrigger data-testid="forecast-select" className="w-auto min-w-[160px] border-[#E4E9F3] bg-white"><SelectValue placeholder="Select forecast" /></SelectTrigger><SelectContent>{items.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent></Select>
            )}
            <Button size="sm" data-testid="add-forecast-btn" onClick={openAdd} className="rounded-full bg-[#1D4ED8] text-white hover:bg-[#1A45C0]"><Plus size={15} /> New Forecast</Button>
          </div>
        }>
        {!selected ? (
          <EmptyState icon={LineChart} title="No forecast yet" description="Create a forecast with your monthly assumptions to project cash." actionLabel="New forecast" onAction={openAdd} />
        ) : (
          <div data-testid="forecast-view">
            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" data-testid="forecast-edit" onClick={openEdit} className="border-[#E4E9F3]"><Pencil size={14} /> Edit assumptions</Button>
              <Button size="sm" variant="outline" data-testid="forecast-delete" onClick={() => setConfirmId(selected.id)} className="border-[#E4E9F3] text-[#DC2626] hover:bg-[#DC2626]/10"><Trash2 size={14} /> Delete</Button>
              <span className="ml-auto text-sm text-[#5A6B8C]">Opening {gbp(selected.opening_balance)} · Closing <span className={closingEnd < 0 ? "font-semibold text-[#DC2626]" : "font-semibold text-[#14245F]"}>{gbp(closingEnd)}</span></span>
            </div>
            {negativeMonth && <p className="mb-3 rounded-lg bg-[#DC2626]/5 px-3 py-2 text-sm font-semibold text-[#DC2626]" data-testid="forecast-warning">Projected to run out of cash by {monthLabel(negativeMonth.month)}.</p>}
            <div className="w-full overflow-x-auto rounded-xl border border-[#E4E9F3]">
              <table className="w-full min-w-[900px] border-collapse text-right text-sm" data-testid="forecast-table">
                <thead>
                  <tr className="bg-[#F7F9FE] text-xs uppercase tracking-[0.08em] text-[#5A6B8C]">
                    <th className="px-3 py-2.5 text-left">Month</th><th className="px-3 py-2.5">Opening</th><th className="px-3 py-2.5">Revenue</th><th className="px-3 py-2.5">Funding</th><th className="px-3 py-2.5">Total In</th><th className="px-3 py-2.5">Salaries</th><th className="px-3 py-2.5">Freelance</th><th className="px-3 py-2.5">Marketing</th><th className="px-3 py-2.5">Software</th><th className="px-3 py-2.5">Other</th><th className="px-3 py-2.5">One-off</th><th className="px-3 py-2.5">Total Out</th><th className="px-3 py-2.5">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.month} className="border-t border-[#E4E9F3]/60">
                      <td className="px-3 py-2.5 text-left font-medium text-[#14245F]">{monthLabel(r.month)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.opening)}</td>
                      <td className="px-3 py-2.5 text-[#16A34A]">{gbp(r.revenue)}</td>
                      <td className="px-3 py-2.5 text-[#16A34A]">{gbp(r.funding)}</td>
                      <td className="px-3 py-2.5 font-medium text-[#14245F]">{gbp(r.totalIn)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.sal)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.free)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.mkt)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.sw)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.other)}</td>
                      <td className="px-3 py-2.5 text-[#5A6B8C]">{gbp(r.oneoff)}</td>
                      <td className="px-3 py-2.5 font-medium text-[#14245F]">{gbp(r.totalOut)}</td>
                      <td className={`px-3 py-2.5 font-semibold ${r.closing < 0 ? "text-[#DC2626]" : "text-[#14245F]"}`}>{gbp(r.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-6 border-t border-[#E4E9F3] pt-4"><UpdatesHistory relatedId={selected.id} relatedType="Forecast" relatedName={selected.name} area="Finance" /></div>
          </div>
        )}
      </SectionCard>

      <EntryModal open={modalOpen} onOpenChange={setModalOpen} title={editing?.id ? "Edit Forecast" : "New Forecast"} submitLabel={editing?.id ? "Save Changes" : "Create Forecast"} fields={forecastFields()} initialValues={editing} onSubmit={save} />
      <ConfirmDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)} title="Delete this forecast?" description="This forecast will be permanently removed." onConfirm={doDelete} />
    </>
  );
};
